import ApiClient from '@videotools/js/api';
import Event from '@videotools/js/api/entities/Event';
import { cloneEventProps } from '@videotools/js/util/cloneEvent';

export class VideoCreateZeroDurationError extends Error {
	constructor(message, zeroDurationTags) {
		super(message);
		this.name = 'CreateVideoZeroDurationError';
		this.tags = zeroDurationTags;
	}

	get count() {
		return this.tags.length;
	}
}

export default {
	namespaced: true,
	state:      () => ({
		sourceEventTags:   [],
		isActive:          false,
		editorAssetVideos: [],
	}),
	mutations: {
		setIsActive(state, flag) {
			state.isActive = flag;
		},
		setSourceEventTags(state, eventTags) {
			state.sourceEventTags = eventTags || [];
		},
		setEditorActiveVideos(state, videos) {
			state.editorAssetVideos = videos || [];
		},
	},
	actions: {
		prepare: ({ commit }, eventTags) => {
			const zeroDurationTags = eventTags.filter(evTag => evTag.duration === 0);

			if (zeroDurationTags.length > 0) {
				throw new VideoCreateZeroDurationError('Cannot create video with zero duration tags', zeroDurationTags);
			}

			commit('setIsActive', true);
			commit('setSourceEventTags', eventTags);
		},
		cancel: ({ commit }) => {
			commit('setIsActive', false);
			commit('setSourceEventTags', []);
		},
		loadEditorAssetVideos: async({ commit }) => {
			const body = await ApiClient.getEditorAssetVideos();
			const videos = body.data.map(data => new Event(data));
			commit('setEditorActiveVideos', videos);
		},
		createVideo: async({ rootGetters }, {
			eventTags,
			name,
			intro,
			outro,
			copyTags,
			categoryParentId,
			enableAudioFade,
			eventTagGraphics,
		}) => {
			const zeroDurationTags = eventTags.filter(evTag => evTag.duration === 0);

			if (zeroDurationTags > 0) {
				throw new VideoCreateZeroDurationError('Cannot create video with zero duration tags');
			}

			const event = rootGetters['event/event'];
			let parts = [];

			for (const tag of eventTags) {
				const tagGraphicIds = eventTagGraphics
					.filter(tagGraphic => tagGraphic.eventTagId === tag.id)
					.map(tagGraphic => tagGraphic.graphicId);

				const part = {
					eventId:      event.id,
					eventTagId:   tag.id,
					startSeconds: tag.originalTimestamp - tag.secondsBefore,
					endSeconds:   tag.originalTimestamp + tag.secondsAfter,
					graphicIds:   tagGraphicIds,
				};
				parts.push(part);
			}
			parts.sort((a, b) => (a.startSeconds > b.startSeconds ? 1 : -1));

			// add intro/outro
			if (intro) {
				parts = [
					{
						eventId:      intro.id,
						startSeconds: 0,
						endSeconds:   intro.duration,
					},
					...parts,
				];
			}

			if (outro) {
				parts = [
					...parts,
					{
						eventId:      outro.id,
						startSeconds: 0,
						endSeconds:   outro.duration,
					},
				];
			}

			try {
				return await ApiClient.buildVideo(cloneEventProps(event, name, categoryParentId), parts, copyTags, enableAudioFade, { suppressErrorToast: true });
			} catch (error) {
				console.error(error);
				throw error;
			}
		},
	},
	getters: {
		isActive:          (state) => state.isActive,
		sourceEventTags:   (state) => state.sourceEventTags,
		editorAssetVideos: (state) => state.editorAssetVideos,
	},
};
